import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { apiUrl } from '../config/api';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setIsError(true);
      setMessage('This password reset link is invalid or has expired.');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setIsError(true);
      setMessage('Please fill in all fields.');
      return;
    }

    if (newPassword.length < 8) {
      setIsError(true);
      setMessage('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setIsError(true);
      setMessage('New passwords do not match.');
      return;
    }

    setLoading(true);
    setMessage(null);
    setIsError(false);

    try {
      const response = await fetch(apiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsError(false);
        setMessage(data.message || 'Your password has been reset successfully. You can now sign in with your new password.');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setIsError(true);
        setMessage(data.message || 'We could not reset your password. Please request a new reset link.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setIsError(true);
      setMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: '480px' }}>
          <div className="auth-header">
            <div className="auth-logo">
              <Lock className="logo-icon" />
              <h1>recberry</h1>
            </div>
            <h2>Choose a new password</h2>
            <p>Enter a secure password that you have not used here before.</p>
          </div>

          {message && (
            <div className={`auth-alert ${isError ? 'error' : 'success'}`}>
              {isError ? <AlertCircle /> : <CheckCircle2 />}
              <span>{message}</span>
            </div>
          )}

          {!token ? (
            <div style={{ padding: '1rem', color: 'var(--gray)', lineHeight: 1.5 }}>
              This password reset link is invalid or has expired. Please request a new link from the sign-in page.
              <div style={{ marginTop: '1.5rem' }}>
                <Link
                  to="/auth"
                  style={{
                    color: 'var(--orange)',
                    fontWeight: 600,
                    textDecoration: 'none'
                  }}
                >
                  ← Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-fields">
                <div className="form-field">
                  <label htmlFor="newPassword">New password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      disabled={loading}
                      style={{ paddingRight: '3rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      disabled={loading}
                      style={{
                        position: 'absolute',
                        right: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--gray)',
                        cursor: 'pointer',
                        padding: '0.25rem'
                      }}
                    >
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="confirmPassword">Confirm new password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      disabled={loading}
                      style={{ paddingRight: '3rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                      style={{
                        position: 'absolute',
                        right: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--gray)',
                        cursor: 'pointer',
                        padding: '0.25rem'
                      }}
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="auth-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="button-icon spinning" />
                    Updating password...
                  </>
                ) : (
                  'Reset password'
                )}
              </button>
            </form>
          )}

          <div style={{
            textAlign: 'center',
            marginTop: '2rem',
            paddingTop: '2rem',
            borderTop: '1px solid #eee',
            color: 'var(--gray)',
            fontSize: '0.875rem'
          }}>
            <Link
              to="/auth"
              style={{
                color: 'var(--gray)',
                textDecoration: 'none',
                fontWeight: 400,
                display: 'inline-block',
                fontSize: '0.8rem'
              }}
            >
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

